import { XMarkIcon } from '@heroicons/react/20/solid';
import { GiftIcon } from '@heroicons/react/24/outline';

import twclsx from 'utils/twclsx';

const defaultIcon = <GiftIcon className="inline size-[24px]" data-testid="nano-banner-default-icon" />;

/**
 * @component NanoBanner
 *
 * NanoBanner - A banner component for displaying promotional messages, alerts, or notifications.
 *
 * @example
 * // Basic usage with main copy only
 * <NanoBanner mainCopy="Free shipping on orders over $50" />
 *
 * @example
 * // Full featured banner with all props
 * <NanoBanner
 *   boldCopy="Limited time:"
 *   className="custom-styles"
 *   handleDismiss={() => setBannerVisible(false)}
 *   icon={<StarIcon className="text-yellow-500" />}
 *   link="Terms & Conditions"
 *   mainCopy="Give friends $15 off their first order"
 *   onClick={() => handleLinkClick()}
 * />
 *
 * @example
 * // Banner without icon
 * <NanoBanner icon={null} mainCopy="New feature available!" />
 *
 * @param {Object} props - Component props
 * @param {string} [props.boldCopy] - Bold text to display before the main copy (e.g., "Limited time:")
 * @param {string} [props.className] - Additional CSS classes to apply to the banner container
 * @param {Function} [props.handleDismiss] - Function called when the dismiss button is clicked. If provided, renders a dismiss button
 * @param {React.ReactNode} [props.icon] - Custom icon to display. If undefined, shows default gift icon. If null, no icon is shown
 * @param {string} [props.link] - Text for the clickable link within the banner
 * @param {string} props.mainCopy - Main text content to display in the banner
 * @param {Function} [props.onClick] - Function called when the link is clicked
 * @param {Object} [props.restProps] - Additional props spread to the banner container div
 *
 * @returns {JSX.Element} A banner component with optional icon, text content, link, and dismiss button
 */
function NanoBanner({ boldCopy, className, handleDismiss, icon, link, mainCopy, onClick, ...restProps }) {
    const isDismissable = typeof handleDismiss === 'function';
    const displayIcon = icon === undefined ? defaultIcon : icon;

    return (
        <section className={twclsx('nano-banner-element bg-brand-secondary w-full p-10', className)} {...restProps}>
            <div className="flex items-center justify-between text-body-mobile-xs md:text-body-mobile-sm">
                <div className="flex flex-1 items-center justify-center gap-10">
                    {displayIcon && (
                        <div className="shrink-0" data-testid="nano-banner-icon">
                            {displayIcon}
                        </div>
                    )}
                    <span className="!leading-[140%]">
                        {boldCopy && <span className="mr-5 font-grotesk-bold">{boldCopy}</span>} {mainCopy}{' '}
                        {link && (
                            <button className="cursor-pointer text-left underline" onClick={onClick} type="button">
                                {link}
                            </button>
                        )}
                    </span>
                </div>
                {isDismissable && (
                    <button type="button" onClick={handleDismiss} className="shrink-0" aria-label="Dismiss banner">
                        <XMarkIcon className="size-[24px]" />
                    </button>
                )}
            </div>
        </section>
    );
}

export default NanoBanner;
